/**
 * FirstTimeAppTour - Interactive spotlight tour for first-time users
 * Points to actual UI elements (menu, sparkle guide) with arrows and explanations
 * Opens the nav menu to show Therappi AI and Settings locations
 * 
 * AUTO-START CONDITIONS (ALL required):
 * 1. User is authenticated
 * 2. conversationalOnboardingCompleted === true (durable flag)
 * 3. tourSeen === false
 * 4. (justCompleted === true OR tourRequested === true) - explicit triggers only
 * 
 * This prevents cold-boot auto-start!
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Menu, Sparkles, ChevronRight, X, MessageCircle,
  Settings, Star, ArrowUp, ArrowDown, ArrowLeft, ArrowRight,
  Activity, Users, Heart, UserCircle, UtensilsCrossed, ClipboardList, TreePine, Stethoscope, ChevronLeft
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { 
  useTourController, 
  isTourRequested, 
  clearTourRequested, 
  isConversationalJustCompleted, 
  clearConversationalJustCompleted,
  isConversationalCompleted,
  isTourSeen
} from '@/hooks/useTourController';
import { useLocation, useNavigate } from 'react-router-dom';
import { diagLog } from '@/utils/diagnosticSession';
import { logFlight, setTourStep as setGlobalTourStep, markTourSeen as markTourSeenState, getOnboardingState, isHydrated as isStateHydrated } from '@/hooks/useOnboardingStateMachine';

const STORAGE_KEY = 'therappi-first-time-tour-seen';

interface TourStep {
  id: string;
  targetSelector: string;
  spotlightPadding?: number;
  title: string;
  titleHasRegistered?: boolean;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  position: 'top' | 'bottom' | 'left' | 'right' | 'center';
  openMenu?: boolean;
  scrollIntoView?: boolean;
  menuCascadeSelectors?: string[];
}

const tourSteps: TourStep[] = [
  {
    id: 'welcome',
    targetSelector: '',
    title: 'Welcome to Therappi',
    titleHasRegistered: true,
    description: "I'm so glad you're here. This is a space designed just for your wellbeing - where psychology and science meet soul.\n\nLet me show you some featured highlights - there's much more to discover once you start exploring!",
    icon: Star,
    position: 'center'
  },
  {
    id: 'guide-button',
    targetSelector: '[data-tour="guide-button"]',
    spotlightPadding: 12,
    title: 'Your Personal Guide',
    titleHasRegistered: false,
    description: "See this sparkle? ✨ That's me - your Therappi Guide. Tap here whenever you need help finding something, understanding a feature, or figuring out what to do next. I'm always just a tap away.",
    icon: Sparkles,
    position: 'bottom'
  },
  {
    id: 'menu-button',
    targetSelector: '[data-tour="menu-button"]',
    spotlightPadding: 8,
    title: 'Your Navigation Hub',
    description: "This menu is your gateway to everything. Wellness tools, tracking, courses, community - it's all organised here.",
    icon: Menu,
    position: 'top'
  },
  {
    id: 'favourites',
    targetSelector: '[data-tour="favourite-star-demo"]',
    spotlightPadding: 12,
    title: 'Your Favourites',
    titleHasRegistered: false,
    description: "See the ☆ stars next to each feature? Tap any star to add that feature to your Favourites section at the top of the menu.\n\n⭐ Your favourites appear first for quick one-tap access\n✨ Personalise your menu by starring what matters most to you",
    icon: Star,
    position: 'right',
    openMenu: true,
    scrollIntoView: true
  },
  {
    id: 'therappi-ai',
    targetSelector: '[data-tour="therappi-ai-link"]',
    spotlightPadding: 6,
    title: 'Meet Therappi AI',
    titleHasRegistered: false,
    description: "Your compassionate AI companion that truly listens, remembers your journey, and helps you see patterns you might miss.\n\n✨ Premium users get deeply personalised guidance based on mood, sleep, and wellness data.",
    icon: MessageCircle,
    position: 'right',
    openMenu: true,
    scrollIntoView: true
  },
  {
    id: 'daily-checkin',
    targetSelector: '[data-tour="checkin-link"]',
    spotlightPadding: 6,
    title: 'Check In, Day & Night',
    titleHasRegistered: false,
    description: "Just a minute or two to track how you feel across body, mind, and spirit. Over time, you'll see powerful patterns emerge.\n\n📊 Discover what helps your mood, sleep, and energy.",
    icon: ClipboardList,
    position: 'left',
    openMenu: true,
    scrollIntoView: true
  },
  {
    id: 'smart-food-scanner',
    targetSelector: '[data-tour="nutrition-link"]',
    spotlightPadding: 6,
    title: 'Scan Your Food',
    titleHasRegistered: false,
    description: "Scan any food to see its impact on mood, gut health, and inflammation. Knowledge becomes power to make changes.",
    icon: UtensilsCrossed,
    position: 'left',
    openMenu: true,
    scrollIntoView: true
  },
  {
    id: 'sanctuary',
    targetSelector: '[data-tour="sanctuary-link"]',
    spotlightPadding: 6,
    title: 'Your Healing Sanctuary',
    titleHasRegistered: false,
    description: "Guided meditations, breathwork, grounding techniques, and more. When life feels overwhelming, this is your calm corner. 🧘",
    icon: TreePine,
    position: 'left',
    openMenu: true,
    scrollIntoView: true
  },
  {
    id: 'whoop-connection',
    targetSelector: '[data-tour="whoop-link"]',
    spotlightPadding: 6,
    title: 'Connect Your Body',
    titleHasRegistered: false,
    description: "Connect your WHOOP to bring biometric data - sleep, recovery, strain, HRV - right into Therappi for insights based on real data.",
    icon: Activity,
    position: 'left',
    openMenu: true,
    scrollIntoView: true
  },
  {
    id: 'community',
    targetSelector: '[data-tour="community-link"]',
    spotlightPadding: 6,
    title: 'Find Your People',
    titleHasRegistered: false,
    description: "Find accountability buddies and join supportive communities who understand your journey.",
    icon: Users,
    position: 'left',
    openMenu: true,
    scrollIntoView: true
  },
  {
    id: 'therapist-connection',
    targetSelector: '[data-tour="clinician-link"]',
    spotlightPadding: 6,
    title: 'Connect with Your Therapist',
    titleHasRegistered: false,
    description: "Working with a therapist? Invite them to Therappi® so they can see your progress, assign exercises, and support you between sessions.\n\n🔒 You control exactly what they see.",
    icon: Stethoscope,
    position: 'left',
    openMenu: true,
    scrollIntoView: true
  },
  {
    id: 'relational',
    targetSelector: '[data-tour="relational-link"]',
    spotlightPadding: 6,
    title: 'Relationships Matter',
    titleHasRegistered: false,
    description: "Invite your partner for couples work, share your journey with loved ones, or explore attachment patterns.",
    icon: Heart,
    position: 'left',
    openMenu: true,
    scrollIntoView: true
  },
  {
    id: 'account-features',
    targetSelector: '[data-tour="settings-button"]',
    spotlightPadding: 6,
    title: 'My Account',
    description: "Customise notifications, manage your subscription, and fine-tune your experience.",
    icon: Settings,
    position: 'left',
    openMenu: true,
    scrollIntoView: true
  },
  {
    id: 'ready',
    targetSelector: '',
    title: "You're All Set!",
    titleHasRegistered: false,
    description: "This was just a sample of what's possible - there's so much more to discover: journaling, dream tracking, courses, clinical tools, and beyond!\n\n☰ Tap the menu anytime to explore everything.\n✨ I'm always here via the sparkle button.",
    icon: Star,
    position: 'center'
  }
];

interface SpotlightPosition {
  top: number;
  left: number;
  width: number;
  height: number;
}

// Animated Arrow Component
const AnimatedArrow: React.FC<{ 
  direction: 'up' | 'down' | 'left' | 'right';
  spotlightPosition: SpotlightPosition;
  stepId?: string;
  openMenu?: boolean;
}> = ({ direction, spotlightPosition, stepId, openMenu }) => {
  const viewportWidth = window.innerWidth;
  const isMobile = viewportWidth < 768;
  
  let effectiveDirection = direction;
  if (isMobile) {
    if (stepId === 'guide-button') {
      effectiveDirection = 'down';
    } else if (openMenu) {
      effectiveDirection = 'left';
    } else if (stepId === 'menu-button') {
      effectiveDirection = 'up';
    }
  }
  
  const ArrowIcon = {
    up: ArrowUp,
    down: ArrowDown,
    left: ArrowLeft,
    right: ArrowRight
  }[effectiveDirection];

  const getArrowPosition = (): React.CSSProperties => {
    const centerX = spotlightPosition.left + spotlightPosition.width / 2;
    const centerY = spotlightPosition.top + spotlightPosition.height / 2;
    
    if (isMobile) {
      if (stepId === 'guide-button') {
        return {
          position: 'fixed',
          left: Math.min(Math.max(centerX - 28, 20), viewportWidth - 76),
          top: spotlightPosition.top - 90,
          zIndex: 10000
        };
      }
      
      if (openMenu) {
        // Step 4 favourites: nudge arrow DOWN by 4px (-2 from baseline) and RIGHT by 6px (-12 from baseline)
        const arrowYOffset = stepId === 'favourites' ? -2 : 0;
        const arrowXOffset = stepId === 'favourites' ? -12 : 0;
        return {
          position: 'fixed',
          left: spotlightPosition.left + spotlightPosition.width + 20 + arrowXOffset,
          top: centerY - 28 + arrowYOffset,
          zIndex: 10000
        };
      }
      
      if (stepId === 'menu-button') {
        return {
          position: 'fixed',
          left: Math.min(Math.max(centerX - 28, 20), viewportWidth - 76),
          top: spotlightPosition.top + spotlightPosition.height + 20,
          zIndex: 10000
        };
      }
      
      return {
        position: 'fixed',
        left: Math.min(Math.max(centerX - 28, 20), viewportWidth - 76),
        top: spotlightPosition.top + spotlightPosition.height + 20,
        zIndex: 10000
      };
    }
    
    switch (effectiveDirection) {
      case 'up':
        return { position: 'fixed', left: centerX - 28, top: spotlightPosition.top + spotlightPosition.height + 20, zIndex: 10000 };
      case 'down':
        return { position: 'fixed', left: centerX - 28, top: spotlightPosition.top - 76, zIndex: 10000 };
      case 'left':
        return { position: 'fixed', left: spotlightPosition.left + spotlightPosition.width + 20, top: centerY - 28, zIndex: 10000 };
      case 'right':
        return { position: 'fixed', left: spotlightPosition.left - 76, top: centerY - 28, zIndex: 10000 };
    }
  };

  const bounceDirection = {
    up: { y: [0, -14, 0] },
    down: { y: [0, 14, 0] },
    left: { x: [0, -14, 0] },
    right: { x: [0, 14, 0] }
  }[effectiveDirection];

  return (
    <motion.div
      style={getArrowPosition()}
      initial={{ opacity: 0, scale: 0.5 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.3 }}
      className="pointer-events-none"
    >
      <motion.div
        animate={bounceDirection}
        transition={{ duration: 0.9, repeat: Infinity, ease: 'easeInOut' }}
        className="relative"
      >
        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary to-[hsl(177,41%,50%)] flex items-center justify-center shadow-xl shadow-primary/50">
          <ArrowIcon className="w-8 h-8 text-primary-foreground" />
        </div>
        <motion.div
          className="absolute inset-0 rounded-full bg-primary"
          animate={{ scale: [1, 2, 1], opacity: [0.5, 0, 0.5] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: 'easeOut' }}
        />
        <motion.div
          className="absolute inset-0 rounded-full bg-primary"
          animate={{ scale: [1, 1.6, 1], opacity: [0.3, 0, 0.3] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: 'easeOut', delay: 0.5 }}
        />
      </motion.div>
    </motion.div>
  );
};

export const FirstTimeAppTour: React.FC = () => {
  const [hasSeenTour, setHasSeenTour] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === 'true'; } catch { return false; }
  });
  const [showTour, setShowTour] = useState(false);
  const showTourRef = useRef(showTour);
  const [currentStep, setCurrentStep] = useState(0);
  const [spotlightPosition, setSpotlightPosition] = useState<SpotlightPosition | null>(null);
  const isAuthenticatedRef = useRef(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [cascadeHighlights, setCascadeHighlights] = useState<string[]>([]);
  const [isCascading, setIsCascading] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const cascadeTimeoutsRef = useRef<NodeJS.Timeout[]>([]);
  const hasAutoStartedRef = useRef(false);
  const hasSubscribedRef = useRef(false);
  const menuClosedForStepRef = useRef<number>(-1);
  const lastActionTimeRef = useRef(0);
  
  const tourController = useTourController();
  const location = useLocation();
  const navigate = useNavigate();
  
  const DEBOUNCE_MS = 300;
  const TRANSITION_MS = 500;
  
  const step = tourSteps[currentStep];
  const isLastStep = currentStep === tourSteps.length - 1;
  const isFirstStep = currentStep === 0;
  const StepIcon = step.icon;

  // Keep ref in sync
  useEffect(() => { showTourRef.current = showTour; }, [showTour]);

  // ============= BOOT DIAGNOSTIC LOG =============
  useEffect(() => {
    const tourSeen = isTourSeen();
    const tourRequested = isTourRequested();
    const conversationalCompleted = isConversationalCompleted();
    const conversationalJustCompleted = isConversationalJustCompleted();
    const welcomeSeen = localStorage.getItem('therappi_welcome_seen_/') === 'true';
    
    const bootDiag = {
      tourSeen,
      tourRequested,
      conversationalCompleted,
      conversationalJustCompleted,
      welcomeSeen,
      isAuthenticated: isAuthenticatedRef.current,
      showTour: showTourRef.current,
      currentStep: 0,
      path: location.pathname,
      timestamp: new Date().toISOString()
    };
    
    console.log('🚀 [BOOT DIAGNOSTIC]', bootDiag);
    logFlight('BOOT_DIAGNOSTIC', bootDiag);
    
    return () => {
      logFlight('TOUR_COMPONENT_UNMOUNT', {
        showTour: showTourRef.current,
        currentStep,
        reason: showTourRef.current ? 'UNEXPECTED_UNMOUNT_DURING_TOUR' : 'normal_unmount'
      });
    };
  }, []);

  // Check authentication status
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      const isAuth = !!session?.user;
      isAuthenticatedRef.current = isAuth;
      setIsAuthenticated(isAuth);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const isAuth = !!session?.user;
      if (!showTourRef.current) {
        isAuthenticatedRef.current = isAuth;
        setIsAuthenticated(isAuth);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const isDashboard = location.pathname === '/' || location.pathname === '/dashboard';

  // ============= ROBUST TARGET DETECTION =============
  const waitForTarget = useCallback((selector: string, maxAttempts = 20, interval = 150): Promise<Element | null> => {
    return new Promise((resolve) => {
      if (!selector) { resolve(null); return; }
      
      let attempts = 0;
      const check = () => {
        attempts++;
        
        // Primary selector check
        let element = document.querySelector(selector);
        
        // Fallback: try data-testid if primary selector failed (for favourite-star-demo)
        if (!element && selector.includes('favourite-star-demo')) {
          element = document.querySelector('[data-testid="favourite-star-demo"]');
          if (element) {
            console.log(`[Tour] Target found via data-testid fallback: ${selector} (attempt ${attempts})`);
          }
        }
        
        if (element) {
          // Check if element is actually visible
          const rect = element.getBoundingClientRect();
          const isVisible = rect.width > 0 && rect.height > 0;
          
          if (isVisible) {
            console.log(`[Tour] Target found and visible: ${selector} (attempt ${attempts})`);
            resolve(element);
            return;
          } else if (attempts < maxAttempts) {
            // Element exists but not visible yet, keep waiting
            console.log(`[Tour] Target found but not visible yet: ${selector} (attempt ${attempts})`);
            setTimeout(check, interval);
            return;
          }
        }
        
        if (attempts >= maxAttempts) {
          console.warn(`[Tour] Target NOT FOUND after ${attempts} attempts: ${selector} - proceeding without spotlight`);
          logFlight('TOUR_TARGET_NOT_FOUND', { selector, attempts });
          resolve(null);
        } else {
          setTimeout(check, interval);
        }
      };
      check();
    });
  }, []);

  // ============= PER-STEP DIAGNOSTIC LOGGING =============
  const logStepDiagnostic = useCallback((context: string, stepIdx: number, extra: Record<string, unknown> = {}) => {
    const s = tourSteps[stepIdx];
    const diagnostic = {
      context,
      stepIndex: stepIdx,
      stepId: s?.id,
      selector: s?.targetSelector || 'center',
      openMenu: s?.openMenu ?? false,
      path: location.pathname,
      menuOpen: isMenuOpen,
      targetFound: s?.targetSelector ? !!document.querySelector(s.targetSelector) : true,
      fallbackUsed: s?.targetSelector ? !document.querySelector(s.targetSelector) : false,
      ...extra
    };
    console.log(`📍 [STEP DIAGNOSTIC] ${context}`, diagnostic);
    logFlight('STEP_DIAGNOSTIC', diagnostic);
  }, [location.pathname, isMenuOpen]);

  // Update spotlight position with double-rAF for layout stability
  const updateSpotlightPosition = useCallback(() => {
    const measure = () => {
      if (!step.targetSelector) {
        setSpotlightPosition(null);
        return;
      }

      const element = document.querySelector(step.targetSelector);
      if (element) {
        const rect = element.getBoundingClientRect();
        const padding = step.spotlightPadding || 8;
        if (step.scrollIntoView) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        
        const newPos = {
          top: rect.top - padding,
          left: rect.left - padding,
          width: rect.width + padding * 2,
          height: rect.height + padding * 2
        };
        
        // Debug log for Step 3 (menu-button) and Step 4 (favourites)
        if (step.id === 'menu-button') {
          console.log('[Tour Step3] menu-button rect', rect, 'padded spotlight', newPos);
        }
        if (step.id === 'favourites') {
          console.log('[Tour Step4] star rect', rect, 'padded spotlight', newPos);
        }
        
        setSpotlightPosition(newPos);
      } else {
        setSpotlightPosition(null);
      }
    };

    // Use double-rAF to ensure layout is fully stable (especially after menu animations)
    requestAnimationFrame(() => {
      requestAnimationFrame(measure);
    });
  }, [step.targetSelector, step.spotlightPadding, step.scrollIntoView, step.id]);

  // Clear cascade timeouts
  const clearCascadeTimeouts = useCallback(() => {
    cascadeTimeoutsRef.current.forEach(t => clearTimeout(t));
    cascadeTimeoutsRef.current = [];
  }, []);

  // ============= START TOUR FUNCTION =============
  const doStartTour = useCallback(() => {
    if (showTourRef.current) {
      logFlight('TOUR_START_SKIPPED', { reason: 'already_showing' });
      return;
    }
    
    logStepDiagnostic('TOUR_STARTING', 0, { trigger: 'doStartTour' });
    
    // CRITICAL: Clear triggers immediately
    clearTourRequested();
    clearConversationalJustCompleted();
    
    setShowTour(true);
    showTourRef.current = true;
    setCurrentStep(0);
    setGlobalTourStep(0);
    tourController.setTourStep(0);
    
    try {
      const popSound = new Audio('/sounds/therappi-guide-pop.wav');
      popSound.volume = 0.5;
      popSound.play().catch(() => {});
    } catch {}
  }, [tourController, logStepDiagnostic]);

  // ============= AUTO-START LOGIC =============
  useEffect(() => {
    if (hasAutoStartedRef.current) return;
    
    const checkAndStartTour = () => {
      if (hasAutoStartedRef.current) return;
      
      const stateMachineStage = getOnboardingState();
      const stateHydrated = isStateHydrated();
      const tourSeen = isTourSeen();
      const tourRequested = isTourRequested();
      const conversationalCompleted = isConversationalCompleted();
      const conversationalJustCompleted = isConversationalJustCompleted();
      
      const checkInfo = {
        isAuth: isAuthenticatedRef.current,
        isDashboard,
        showTour: showTourRef.current,
        stateMachineStage,
        stateHydrated,
        tourRequested,
        tourSeen,
        conversationalCompleted,
        conversationalJustCompleted,
        timestamp: new Date().toISOString()
      };
      
      console.log('🔍 [FirstTimeAppTour] AUTO-START CHECK', checkInfo);
      logFlight('TOUR_AUTO_START_CHECK', checkInfo);
      
      // NEW LOGIC: Use state machine as primary trigger
      // If state machine says we're in 'tour' stage, that's the authoritative signal
      const inTourStage = stateHydrated && stateMachineStage === 'tour';
      
      // CRITICAL CONDITIONS for auto-start:
      // 1. User is authenticated
      // 2. On dashboard
      // 3. Tour not already showing
      // 4. Tour not already seen
      // 5. EITHER: state machine says 'tour' stage, OR explicit flags (conversationalJustCompleted/tourRequested)
      const hasExplicitTrigger = tourRequested || conversationalJustCompleted;
      
      const shouldAutoStart = 
        isAuthenticatedRef.current && 
        isDashboard && 
        !showTourRef.current && 
        !tourSeen && 
        (inTourStage || (conversationalCompleted && hasExplicitTrigger));
      
      if (shouldAutoStart) {
        hasAutoStartedRef.current = true;
        const reason = inTourStage ? 'STATE_MACHINE_TOUR_STAGE' : 
                       tourRequested ? 'TOUR_REQUESTED_FLAG' : 'CONVERSATIONAL_JUST_COMPLETED';
        console.log('🚀 [FirstTimeAppTour] Starting tour', { reason });
        logFlight('TOUR_AUTO_START_TRIGGERED', { reason, ...checkInfo });
        
        tourController.prepareTour();
        doStartTour();
      } else if (!hasExplicitTrigger && !inTourStage && !tourSeen && conversationalCompleted && isAuthenticatedRef.current) {
        console.log('⏸️ [FirstTimeAppTour] Cold boot detected - waiting for explicit trigger or tour stage');
        logFlight('TOUR_COLD_BOOT_BLOCKED', checkInfo);
      }
    };
    
    const timer1 = setTimeout(checkAndStartTour, 100);
    const timer2 = setTimeout(checkAndStartTour, 500);
    const timer3 = setTimeout(checkAndStartTour, 1000);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [isDashboard, doStartTour, tourController]);

  // ============= EVENT SUBSCRIPTION (ONCE ON MOUNT) =============
  useEffect(() => {
    if (hasSubscribedRef.current) return;
    hasSubscribedRef.current = true;
    
    const unsubscribe = tourController.subscribe((action) => {
      if (action === 'start-tour') {
        if (isAuthenticatedRef.current && !showTourRef.current) {
          hasAutoStartedRef.current = true;
          doStartTour();
        }
      }
    });
    
    return () => unsubscribe();
  }, []);

  // ============= STEP TRANSITION EFFECT =============
  useEffect(() => {
    if (!showTour) return;
    
    logStepDiagnostic('STEP_TRANSITION', currentStep);
    
    clearCascadeTimeouts();
    setIsCascading(false);
    setCascadeHighlights([]);
    
    // CRITICAL: Clear spotlight immediately to prevent ghost from previous step
    setSpotlightPosition(null);
    
    const openMenu = step.openMenu;
    const targetSelector = step.targetSelector;
    
    let isCancelled = false;
    
    if (openMenu) {
      // CRITICAL: Open menu FIRST (will be no-op if already open)
      tourController.openMenu();
      setIsMenuOpen(true);
      
      // Use longer delay for Step 5 to allow menu translateY animation to settle
      const stepId = step.id;
      const delay = stepId === 'therappi-ai' ? 600 : 400;
      
      const timer = setTimeout(async () => {
        if (isCancelled) return;
        
        // Wait for target with polling
        const targetElement = await waitForTarget(targetSelector);
        
        logStepDiagnostic('TARGET_DETECTION_RESULT', currentStep, {
          targetFound: !!targetElement,
          fallbackUsed: !targetElement && !!targetSelector
        });
        
        if (isCancelled) return;
        
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'auto', block: 'center' });
        }
        
        // Double-rAF for stable measurement after layout changes
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (!isCancelled) {
              updateSpotlightPosition();
            }
          });
        });
      }, delay);
      
      return () => { isCancelled = true; clearTimeout(timer); };
    } else {
      if (menuClosedForStepRef.current !== currentStep) {
        menuClosedForStepRef.current = currentStep;
        setIsMenuOpen(false);
      }
      
      const timer = setTimeout(async () => {
        if (isCancelled) return;
        
        if (targetSelector) {
          await waitForTarget(targetSelector);
        }
        
        if (!isCancelled) {
          updateSpotlightPosition();
        }
      }, 300);
      
      return () => { isCancelled = true; clearTimeout(timer); };
    }
  }, [showTour, currentStep]);

  // Update spotlight on window events (resize, orientation, scroll)
  useEffect(() => {
    if (!showTour) return;
    
    const handleUpdate = () => {
      // Double-rAF for stable measurement after layout changes
      requestAnimationFrame(() => {
        requestAnimationFrame(updateSpotlightPosition);
      });
    };
    
    window.addEventListener('resize', handleUpdate);
    window.addEventListener('orientationchange', handleUpdate);
    window.addEventListener('scroll', handleUpdate, true);
    
    // For Step 3 (menu-button), also observe the target element for layout changes
    const stepId = tourSteps[currentStep]?.id;
    let resizeObserver: ResizeObserver | null = null;
    
    if (stepId === 'menu-button') {
      const menuButton = document.querySelector('[data-tour="menu-button"]');
      if (menuButton) {
        resizeObserver = new ResizeObserver(() => {
          console.log('[Tour Step3] ResizeObserver triggered re-measure');
          handleUpdate();
        });
        resizeObserver.observe(menuButton);
        
        // Also listen for transitionend on menu button or parent
        const handleTransitionEnd = (e: TransitionEvent) => {
          if (e.propertyName === 'transform' || e.propertyName === 'opacity') {
            console.log('[Tour Step3] transitionend triggered re-measure');
            handleUpdate();
          }
        };
        menuButton.addEventListener('transitionend', handleTransitionEnd as EventListener);
        
        // Cleanup transitionend
        return () => {
          window.removeEventListener('resize', handleUpdate);
          window.removeEventListener('orientationchange', handleUpdate);
          window.removeEventListener('scroll', handleUpdate, true);
          resizeObserver?.disconnect();
          menuButton.removeEventListener('transitionend', handleTransitionEnd as EventListener);
        };
      }
    }
    
    return () => {
      window.removeEventListener('resize', handleUpdate);
      window.removeEventListener('orientationchange', handleUpdate);
      window.removeEventListener('scroll', handleUpdate, true);
      resizeObserver?.disconnect();
    };
  }, [showTour, currentStep, updateSpotlightPosition]);

  // ============= NAVIGATION HANDLERS =============
  // handleDismiss first since handleNext depends on it
  const handleDismiss = useCallback((reason: 'user_completed' | 'user_skipped' = 'user_completed') => {
    logFlight('TOUR_EXITED_REASON', { reason, currentStep, stepId: step.id });
    
    clearCascadeTimeouts();
    if (isMenuOpen) {
      tourController.closeMenu();
      setIsMenuOpen(false);
    }
    
    setShowTour(false);
    showTourRef.current = false;
    setHasSeenTour(true);
    setGlobalTourStep(0);
    
    // CRITICAL: Set BOTH completion keys to prevent re-run and navigation deadlock
    try { localStorage.setItem(STORAGE_KEY, 'true'); } catch {}
    try { localStorage.setItem('therappi_tour_completed', 'true'); } catch {}
    
    markTourSeenState();
    tourController.endTour();
    
    // Navigate to Daily Habits
    navigate('/');
  }, [isMenuOpen, tourController, clearCascadeTimeouts, currentStep, step.id, navigate]);

  const handleNext = useCallback(() => {
    const now = Date.now();
    if (now - lastActionTimeRef.current < DEBOUNCE_MS) {
      console.log('[Tour] handleNext DEBOUNCED');
      return;
    }
    lastActionTimeRef.current = now;
    
    if (isTransitioning || isCascading) {
      console.log('[Tour] handleNext blocked - transitioning:', isTransitioning, 'cascading:', isCascading);
      return;
    }
    
    logStepDiagnostic('HANDLE_NEXT', currentStep, { isLast: isLastStep });
    
    if (isLastStep) {
      handleDismiss('user_completed');
    } else {
      setIsTransitioning(true);
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      setGlobalTourStep(nextStep);
      tourController.setTourStep(nextStep);
      // Shorter transition for snappier feel
      setTimeout(() => setIsTransitioning(false), 300);
    }
  }, [currentStep, isTransitioning, isCascading, isLastStep, handleDismiss, logStepDiagnostic, tourController]);

  const handleBack = useCallback(() => {
    const now = Date.now();
    if (now - lastActionTimeRef.current < DEBOUNCE_MS) {
      console.log('[Tour] handleBack DEBOUNCED');
      return;
    }
    lastActionTimeRef.current = now;
    
    if (isTransitioning || isCascading) {
      console.log('[Tour] handleBack blocked - transitioning:', isTransitioning, 'cascading:', isCascading);
      return;
    }
    
    if (currentStep > 0) {
      logStepDiagnostic('HANDLE_BACK', currentStep - 1, {});
      setIsTransitioning(true);
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      setGlobalTourStep(prevStep);
      tourController.setTourStep(prevStep);
      setTimeout(() => setIsTransitioning(false), 300);
    }
  }, [currentStep, isTransitioning, isCascading, logStepDiagnostic, tourController]);

  // ============= POSITIONING =============
  const getArrowDirection = (): 'up' | 'down' | 'left' | 'right' | null => {
    if (!spotlightPosition || step.position === 'center') return null;
    switch (step.position) {
      case 'bottom': return 'up';
      case 'top': return 'down';
      case 'left': return 'right';
      case 'right': return 'left';
      default: return null;
    }
  };

  const getTooltipStyle = (): React.CSSProperties => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const isMobile = viewportWidth < 768;
    const safeMargin = 16;
    const bottomBuffer = 100;

    const baseStyle: React.CSSProperties = {
      position: 'fixed',
      maxWidth: isMobile ? 'calc(100vw - 32px)' : 420,
      width: isMobile ? 'auto' : 400,
      zIndex: 9999,
    };

    if (isMobile) {
      if (stepId === 'guide-button') {
        return { ...baseStyle, left: safeMargin, right: safeMargin, top: safeMargin, width: 'auto' };
      }
      if (stepId === 'menu-button') {
        return { ...baseStyle, left: safeMargin, right: safeMargin, bottom: bottomBuffer, top: 'auto', width: 'auto' };
      }
      // Step 13 (My Account): position card at top so account button is visible below
      if (stepId === 'account-features') {
        return { ...baseStyle, left: safeMargin, right: safeMargin, top: 'calc(env(safe-area-inset-top, 16px) + 96px)', bottom: 'auto', width: 'auto' };
      }
      if (step.openMenu) {
        // All other menu steps (4-12, 14): use consistent safe-area bottom buffer
        return { ...baseStyle, left: safeMargin, right: safeMargin, bottom: 'calc(env(safe-area-inset-bottom, 16px) + 0px)', top: 'auto', width: 'auto', maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' };
      }
      // Step 1 only: position higher with safe-area-respecting bottom buffer
      if (currentStep === 0) {
        return { ...baseStyle, left: safeMargin, right: safeMargin, bottom: 'calc(env(safe-area-inset-bottom, 16px) + 24px)', top: 'auto', width: 'auto' };
      }
      // Step 2 only: position card lower (top: 48% + 120px to move down ~1 inch)
      if (currentStep === 1) {
        return { ...baseStyle, left: safeMargin, right: safeMargin, top: 'calc(48% + 120px)', transform: 'translateY(-50%)', width: 'auto' };
      }
      return { ...baseStyle, left: safeMargin, right: safeMargin, top: '45%', transform: 'translateY(-50%)', width: 'auto' };
    }

    if (step.position === 'center' || !spotlightPosition) {
      return { ...baseStyle, left: '50%', top: '50%', transform: 'translate(-50%, -50%)' };
    }

    if (step.openMenu && spotlightPosition) {
      const rightEdge = spotlightPosition.left + spotlightPosition.width;
      return { ...baseStyle, left: Math.max(safeMargin, rightEdge + 30), top: Math.max(safeMargin, spotlightPosition.top) };
    }

    switch (step.position) {
      case 'bottom': return { ...baseStyle, left: '50%', transform: 'translateX(-50%)', top: safeMargin + 30 };
      case 'top': return { ...baseStyle, left: '50%', transform: 'translateX(-50%)', bottom: bottomBuffer, top: 'auto' };
      default: return { ...baseStyle, left: '50%', transform: 'translateX(-50%)', top: safeMargin + 40 };
    }
  };

  const stepId = step.id;
  const arrowDirection = getArrowDirection();

  if (!showTour) return null;

  return (
    <AnimatePresence>
      {showTour && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[300]"
        >
          {/* Dark overlay with spotlight cutout - unified for ALL steps */}
          <>
            <svg className="absolute inset-0 w-full h-full pointer-events-none">
              <defs>
                <mask id="spotlight-mask">
                  <rect width="100%" height="100%" fill="white" />
                  {spotlightPosition && (
                    <rect
                      x={spotlightPosition.left + (stepId === 'menu-button' ? 4 : (stepId === 'favourites' || step.openMenu) ? 10 : 0)}
                      y={spotlightPosition.top}
                      width={spotlightPosition.width}
                      height={spotlightPosition.height}
                      rx={12}
                      fill="black"
                    />
                  )}
                </mask>
              </defs>
              <rect width="100%" height="100%" fill="rgba(0, 0, 0, 0.5)" mask="url(#spotlight-mask)" />
            </svg>

            {/* Spotlight ring animation - same component for ALL steps */}
            {spotlightPosition && !isCascading && (
              <motion.div
                key={`spotlight-${stepId}`}
                initial={{ scale: 1.1, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="absolute rounded-lg border-2 border-primary pointer-events-none"
                style={{
                  top: spotlightPosition.top,
                  left: spotlightPosition.left + (stepId === 'menu-button' ? 4 : (stepId === 'favourites' || step.openMenu) ? 10 : 0),
                  width: spotlightPosition.width,
                  height: spotlightPosition.height,
                  zIndex: 340
                }}
              >
                <motion.div
                  animate={{ boxShadow: ['0 0 0 0 hsl(var(--primary) / 0.4)', '0 0 0 8px hsl(var(--primary) / 0)'] }}
                  transition={{ duration: 1.2, repeat: Infinity }}
                  className="absolute inset-0 rounded-lg"
                />
              </motion.div>
            )}
          </>

          {/* Animated Arrow */}
          {spotlightPosition && arrowDirection && !isCascading && (
            <AnimatedArrow 
              direction={arrowDirection} 
              spotlightPosition={spotlightPosition}
              stepId={stepId}
              openMenu={step.openMenu}
            />
          )}

          {/* Info Card */}
          <motion.div
            key={`card-${stepId}`}
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            style={getTooltipStyle()}
            className="bg-card/95 backdrop-blur-md rounded-2xl shadow-2xl border border-border/50 overflow-hidden"
          >
            {/* Header - Centered layout with step badge */}
            <div className="p-4 bg-gradient-to-r from-primary/10 to-accent/10 border-b border-border/30 relative">
              {/* Step badge - top left */}
              <div className="absolute top-3 left-3">
                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-primary/20 text-primary">
                  {currentStep + 1}/{tourSteps.length}
                </span>
              </div>
              
              {/* Close button - top right */}
              <button
                onClick={() => handleDismiss('user_skipped')}
                className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                aria-label="Close tour"
              >
                <X className="w-4 h-4" />
              </button>
              
              {/* Centered icon and title */}
              <div className="flex flex-col items-center text-center pt-4">
                <div 
                  className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center mb-3"
                  style={currentStep === 1 ? { transform: 'translateX(-8px)' } : undefined}
                >
                  <StepIcon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold text-foreground text-lg">
                  {step.title}
                  {step.titleHasRegistered && <span className="text-xs align-super">®</span>}
                </h3>
              </div>
            </div>

            {/* Content - centered */}
            <div className="p-4">
              <p className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed text-center">
                {step.description}
              </p>
            </div>

            {/* Footer with navigation */}
            <div className="p-4 pt-0 pointer-events-auto">
              {/* Vertical progress bars - centered */}
              <div className="flex justify-center gap-1 mb-4">
                {tourSteps.map((_, idx) => (
                  <div
                    key={idx}
                    className={cn(
                      "w-1 h-4 rounded-full transition-colors",
                      idx === currentStep ? "bg-primary" : idx < currentStep ? "bg-primary/50" : "bg-muted"
                    )}
                  />
                ))}
              </div>
              
              {/* Full-width buttons */}
              <div className="flex flex-col gap-2">
              {isFirstStep ? (
                  /* First step: "Show Me Around" button + subtle skip link */
                  <>
                    <Button
                      onClick={handleNext}
                      disabled={isTransitioning}
                      className="w-full bg-primary hover:bg-primary/90"
                    >
                      Show Me Around
                    </Button>
                    <button
                      onClick={() => handleDismiss('user_skipped')}
                      className="text-xs text-muted-foreground/70 hover:text-muted-foreground transition-colors mt-1"
                    >
                      I'll explore on my own
                    </button>
                  </>
                ) : (
                  /* Other steps: Back and Continue buttons */
                  <>
                    <Button
                      onClick={handleNext}
                      disabled={isTransitioning}
                      className="w-full bg-primary hover:bg-primary/90"
                    >
                      {isLastStep ? "Let's Go!" : "Continue"}
                      {!isLastStep && <ChevronRight className="w-4 h-4 ml-1" />}
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={handleBack}
                      disabled={isTransitioning}
                      className="w-full"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Back
                    </Button>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
